import Sentry from '@/services/sentry'
import SlideApi from '@/api/slide'
import Socket from '@/socket'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import { ALLOW_PICKING_MORE_OPTION } from '@/constant/eventActions'

const patchSlideByIdAction = ({ commit, state }, slide) => {
  const { id: slideId } = slide

  if (slideId === state.activeSlideId) {
    commit('updateSlideActive', slide)
    return
  }

  const { slides } = state.presentation

  const newSlides = slides.map((item) => {
    if (item.id !== slideId) return item

    return { ...item, ...slide }
  })

  commit('setSlides', newSlides)
}

const updateSlidesVersionAction = ({ commit, state }, { newSlideVersions }) => {
  if (!newSlideVersions || !newSlideVersions.length) return
  newSlideVersions.forEach((slide) => {
    if (state.activeSlideId == slide.id) {
      commit('updateSlideActive', {
        id: slide.id,
        version: slide.version,
      })
    }
    commit('updateSlideById', {
      id: slide.id,
      version: slide.version,
    })
  })
}

const updateSlideActiveActionV2 = async ({ commit, state }, { slide, $optimistic = true }) => {
  const slideId = slide.id || state.slideActive.id
  if (!slideId) return
  if ($optimistic) {
    commit('updateSlideActive', slide)
  }

  await SlideApi.updateSlideById(slideId, slide)
  commit('updateSlideActive', slide)
}

// action này dùng để fix việc có order trong 1 slide bị sét về null hoặc 0 làm cho người dùng ko thể sắp xếp đc thứ tự slides
// thì sẽ check nếu có null hoặc 0 thì sẽ đánh lại orders theo danh sách hiển thị trên màn hình hiện tại khi người dùng mở presentation editor
const recheckAndFixCorrectOrderSlidesAction = async ({ commit, state, dispatch }) => {
  const { slides } = state.presentation
  const isCheckOrderNotSet = slides.some((item) => !item.order)
  if (!isCheckOrderNotSet) return
  slides.forEach((item, key) => {
    item.order = key + 1
  })
  const slideSorted = slides.map((item) => {
    return { id: item.id, order: item.order }
  })
  dispatch('updatePresentationAction', {
    slides,
    Slides: slides,
  })
  const indexSlideActive = slides.findIndex((item) => item.id === state.slideActive.id)
  if (indexSlideActive !== -1) {
    commit('updateSlideActive', {
      order: slides[indexSlideActive].order,
    })
  }
  const payload = {
    sort: slideSorted,
    accessCode: state.presentation.uniqueAccessCode,
    socketId: Socket.getSocketId(),
  }
  await SlideApi.sortSlides(state.presentation.id, payload)
}

// Re-derives slideActive.multipleChoice and slideActive.isCorrectGetPoint from
// the live count of correct SlideOptions, then commits + PUTs to the backend if
// they would change. Call this from any path that mutates SlideOptions outside
// of the correct-checkbox toggle (deletions, undo/redo, image/text removal that
// auto-clears `correct`). Without this sync the persisted flags drift from the
// slide's actual content — see AHA-42286.
//
// Replaces the previous SlideOptionsInputV2._updateMultipleChoice() →
// updateSettingSlide(type=1) → _updateSlide chain, so it also handles the side
// effects that chain produced: the sync-loading indicator, limitChoice in the
// payload for pickAnswer slides, and the ALLOW_PICKING_MORE_OPTION analytics
// event when multipleChoice flips.
const syncMultipleChoiceFromOptionsAction = async ({
  commit,
  dispatch,
  state,
  getters,
}) => {
  const slide = state.slideActive
  if (!slide || !slide.id || !Array.isArray(slide.SlideOptions)) return

  const correctCount = slide.SlideOptions.filter((option) => option.correct).length
  const newMultipleChoice = correctCount > 1
  const updates = {}

  if (slide.multipleChoice !== newMultipleChoice) {
    updates.multipleChoice = newMultipleChoice
  }
  // 1→2 transition: auto-enable partial scoring (preserve existing behaviour
  // of SlideOptionsInputV2._updateMultipleChoice).
  if (correctCount === 2 && !slide.multipleChoice && !slide.isCorrectGetPoint) {
    updates.isCorrectGetPoint = true
  }
  // Drop partial scoring when the slide no longer has multiple correct answers
  // — otherwise the persisted flag still drives audience-side multi-select UI
  // and "any wrong pick = 0" scoring (AHA-42286).
  if (correctCount < 2 && slide.isCorrectGetPoint) {
    updates.isCorrectGetPoint = false
  }

  if (Object.keys(updates).length === 0) return

  commit('updateSlideActive', updates)
  dispatch('setSyncLoadingAction', 1)

  try {
    const payload = {
      ...updates,
      accessCode: state.presentation.uniqueAccessCode,
      socketId: Socket.getSocketId(),
      presenting: getters.isPresenting,
    }
    if (slide.type === 'pickAnswer') {
      payload.limitChoice = slide.SlideOptions.length
    }
    await SlideApi.updateSlideById(slide.id, payload)
    dispatch('setSyncLoadingAction', 2)

    if (Object.prototype.hasOwnProperty.call(updates, 'multipleChoice')) {
      Utils.trackGA4AndMixpanel(ALLOW_PICKING_MORE_OPTION, {
        eventAction: ALLOW_PICKING_MORE_OPTION,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        eventLabel: `Allow picking more than one option: ${newMultipleChoice}`,
        presentationId: state.presentation.id,
        slideType: slide.type,
        slideId: slide.id,
      })
    }
  } catch (error) {
    Sentry.captureException(error)
    dispatch('setSyncLoadingAction', 3)
  }
}

const clearSlideDataAction = async ({ state, dispatch }, { slideId }) => {
  const payload = {
    socketId: Socket.getSocketId(),
    presentationId: state.presentation.id,
    slideId,
  }
  const response = await SlideApi.clearSlides(payload)

  dispatch('updateSlidesVersionAction', {
    newSlideVersions: response.data.newSlideVersions,
  })
}

export default {
  patchSlideByIdAction,
  updateSlidesVersionAction,
  updateSlideActiveActionV2,
  recheckAndFixCorrectOrderSlidesAction,
  syncMultipleChoiceFromOptionsAction,
  clearSlideDataAction,
}
